import _ from 'lodash';
import React, {FC} from 'react';

import {ChannelTypesDropdown} from '../../../../../../../../../../src/components/common/dropdowns/channelTypes/channelTypesDropdown';
import {
  getContactSourceFromMessageType,
  getMessageTypeFromChannelType,
} from '../../../../../../../../../../src/helpers/modelHelpers';
import {CardModel} from '../../../../../../../../../../src/models/api/cardModel';
import {ChannelTypesEnum} from '../../../../../../../../../../src/models/api/channelCommonTypes';
import {Trans} from '../../../../../../../../../shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {PopoverCoordinator} from '../../../../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {useContactsManagerContext} from '../../../../contactsManagerContext';
import {buildContactsManagerMessageEvent} from '../../../../helpers/trackingHelpers';

/*
 * Props.
 */

export type ChannelTypesToIdMap = Record<ChannelTypesEnum, ReadonlyArray<number>>;

interface ContactsTableMessageActionProps {
  selectedCards: ReadonlyArray<CardModel>;
  channelTypes: ReadonlyArray<ChannelTypesEnum>;
  channelTypesToId: ChannelTypesToIdMap;
  onComposeMessage: (contactUids: ReadonlyArray<string>, channelId?: number) => void;
}

/*
 * Component.
 */

export const ContactsTableMessageAction: FC<ContactsTableMessageActionProps> = (props) => {
  const {selectedCards, channelTypes, channelTypesToId, onComposeMessage} = props;
  const isDisabled = channelTypes.length === 0;
  const {onTrackHandler} = useContactsManagerContext();

  const onMessageClick = (type?: ChannelTypesEnum) => {
    const channelType = type ?? channelTypes[0];
    const contactSource = getContactSourceFromChannelType(channelType);
    onComposeMessage(
      _(selectedCards)
        .map((card) => card.contactUids.find((contactUid) => contactUid.startsWith(contactSource)))
        .flatten()
        .compact()
        .value(),
      getChannelIdToUse(channelType, channelTypesToId),
    );
    onTrackHandler(buildContactsManagerMessageEvent(selectedCards.length));
  };

  if (channelTypes.length <= 1) {
    return renderMessageButton(isDisabled, false, onMessageClick);
  }
  return renderContactSourceDropdownButton(onMessageClick, channelTypes, isDisabled);
};

/*
 * Helpers.
 */

function renderMessageButton(
  isDisabled: boolean,
  hasHover?: boolean,
  onMessageClick?: HandlerOf<ChannelTypesEnum | undefined>,
) {
  return (
    <Button
      variant={ButtonVariantsEnum.SECONDARY}
      onClick={() => {
        if (onMessageClick) {
          onMessageClick(undefined);
        }
      }}
      isDisabled={isDisabled}
      hasHover={hasHover}
    >
      <Trans id="contact-manager-table-bulk-actions-message">Message</Trans>
    </Button>
  );
}

function renderContactSourceDropdownButton(
  onMessageClick: HandlerOf<ChannelTypesEnum | undefined>,
  channelTypes: ReadonlyArray<ChannelTypesEnum>,
  isDisabled: boolean,
) {
  return (
    <PopoverCoordinator
      renderButton={(hasHover) => renderMessageButton(isDisabled, hasHover)}
      renderModal={(popoverProps) => (
        <ChannelTypesDropdown
          channelTypes={channelTypes}
          popoverProps={popoverProps}
          onSourceSelect={onMessageClick}
        />
      )}
    />
  );
}

function getContactSourceFromChannelType(channelType: ChannelTypesEnum) {
  return getContactSourceFromMessageType(getMessageTypeFromChannelType(channelType));
}

function getChannelIdToUse(channelType: ChannelTypesEnum, channelTypesToId: ChannelTypesToIdMap) {
  // If we have an email channel, let the composer select the channel.
  if (channelType === ChannelTypesEnum.SMTP) {
    return undefined;
  }
  return channelTypesToId[channelType][0];
}
